import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type { AdminV2MessageTemplateLibraryCategory } from "@/types/adminV2/messaging";

export interface MessagingTemplateFolder {
  id: string;
  organisationId: string | null;
  parentId: string | null;
  libraryCategory: AdminV2MessageTemplateLibraryCategory;
  name: string;
  version: number;
}
export type MessagingTemplateFolderScope =
  { kind: "global" } | { kind: "organisation"; organisationId: string };
export interface MessagingTemplateFolderContext {
  /** Local request ownership, included in cache identity but never sent. */
  ownerKey: string;
  scope: MessagingTemplateFolderScope;
  libraryCategory?: AdminV2MessageTemplateLibraryCategory;
}
export interface MessagingTemplateOrganisationOption {
  id: string;
  name: string;
  shortCode: string | null;
}
export interface CreateFolder extends MessagingTemplateFolderContext {
  libraryCategory: Exclude<AdminV2MessageTemplateLibraryCategory, "system">;
  name: string;
  parentId: string | null;
}
export interface UpdateFolder extends MessagingTemplateFolderContext {
  folderId: string;
  expectedVersion: number;
  name?: string;
  parentId?: string | null;
}
export interface DeleteFolder extends MessagingTemplateFolderContext {
  folderId: string;
  expectedVersion: number;
}

const base = "/admin/v2/messaging/template-library";
const scopeParams = ({
  scope,
  libraryCategory,
}: MessagingTemplateFolderContext) => ({
  ...(libraryCategory ? { libraryCategory } : {}),
  ...(scope.kind === "global"
    ? { scope: "global" }
    : { scope: "organisation", organisationId: scope.organisationId }),
});
const treeTags = ({ scope }: MessagingTemplateFolderContext) => [
  {
    type: "AdminV2MessagingTemplates" as const,
    id: `FOLDERS:${scope.kind === "global" ? "global" : scope.organisationId}`,
  },
];
const invalidatesTree = (
  result: unknown,
  error: unknown,
  context: MessagingTemplateFolderContext,
) => {
  const reload =
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error.status === 409 || error.status === 404);
  return result !== undefined || reload
    ? [
        ...treeTags(context),
        ...(reload
          ? [{ type: "AdminV2MessagingTemplates" as const, id: "LIST" }]
          : []),
      ]
    : [];
};
// Deletion roots direct placements. Active root/folder pages must refetch in
// the same document; tree refresh alone leaves their cached membership stale.
const invalidatesDeletedFolder = (
  result: unknown,
  error: unknown,
  context: MessagingTemplateFolderContext,
) => {
  const tags = invalidatesTree(result, error, context);
  return result !== undefined
    ? [
        ...treeTags(context),
        { type: "AdminV2MessagingTemplates" as const, id: "LIST" },
      ]
    : tags;
};

/** Folder-only transport. No template placement or origin authority. */
export const buildAdminV2MessagingTemplateFolderEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2MessagingTemplateOrganisationOptions: builder.query<
    {
      organisations: MessagingTemplateOrganisationOption[];
      pagination: {
        limit: number;
        offset: number;
        total: number;
        hasMore: boolean;
      };
    },
    { ownerKey: string; q?: string; limit: number; offset: number }
  >({
    query: ({ q, limit, offset }) => ({
      url: `${base}/organisation-options`,
      params: { ...(q ? { q } : {}), limit, offset },
    }),
    keepUnusedDataFor: 0,
  }),
  getAdminV2MessagingTemplateFolders: builder.query<
    {
      folders: MessagingTemplateFolder[];
    },
    MessagingTemplateFolderContext
  >({
    query: (context) => ({
      url: `${base}/folders`,
      params: scopeParams(context),
    }),
    providesTags: (_result, _error, context) => treeTags(context),
    keepUnusedDataFor: 0,
  }),
  createAdminV2MessagingTemplateFolder: builder.mutation<
    MessagingTemplateFolder,
    CreateFolder
  >({
    query: ({ scope, name, parentId, libraryCategory }) => ({
      url: `${base}/folders`,
      method: "POST",
      data: {
        organisationId: scope.kind === "global" ? null : scope.organisationId,
        parentId,
        libraryCategory,
        name,
      },
    }),
    invalidatesTags: invalidatesTree,
  }),
  updateAdminV2MessagingTemplateFolder: builder.mutation<
    MessagingTemplateFolder,
    UpdateFolder
  >({
    query: ({ folderId, expectedVersion, name, parentId }) => ({
      url: `${base}/folders/${encodeURIComponent(folderId)}`,
      method: "PATCH",
      data: {
        expectedVersion,
        ...(name !== undefined ? { name } : {}),
        ...(parentId !== undefined ? { parentId } : {}),
      },
    }),
    invalidatesTags: invalidatesTree,
  }),
  deleteAdminV2MessagingTemplateFolder: builder.mutation<
    {
      id: string;
      parentId: string | null;
    },
    DeleteFolder
  >({
    query: ({ folderId, expectedVersion }) => ({
      url: `${base}/folders/${encodeURIComponent(folderId)}`,
      method: "DELETE",
      data: { expectedVersion },
    }),
    invalidatesTags: invalidatesDeletedFolder,
  }),
});
